import { QUERY_RESPONSE_RECEIVED } from '@web-platform/shared-universal-constants/src/network';
import { PARTNER_METADATA } from '@web-platform/shared-universal-types/src/network/requestTypes';

import type { QueryResponseActionPartnerMetadataPayload } from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type {
  PartnerMetadataBookingPanel,
  PartnerMetadataStateBookingPanel,
} from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/partnerMetadataBookingPanel';

export const INITIAL_STATE: PartnerMetadataStateBookingPanel = { byId: {} };

type PartnerMetadataQueryResponseAction = {
  type: 'QUERY_RESPONSE_RECEIVED';
  error: boolean;
  payload: QueryResponseActionPartnerMetadataPayload;
};

type PartnerMetadataAction = PartnerMetadataQueryResponseAction | { type: string };

const byId = (
  state: PartnerMetadataBookingPanel,
  action: PartnerMetadataQueryResponseAction,
): PartnerMetadataBookingPanel => {
  switch (action.payload.type) {
    case PARTNER_METADATA: {
      const { response } = action.payload;

      if (!response.headers || !response.body?.partnerMetadata) {
        return state;
      }

      const { partnerMetadata } = response.body;

      const transformedPartnerMetadata = Object.keys(partnerMetadata).reduce(
        (newState: PartnerMetadataBookingPanel, agentId) => {
          const trustedLevel = partnerMetadata[agentId].partnerTrustData?.trustedLevel;

          if (trustedLevel) {
            return {
              ...newState,
              [agentId]: {
                trust_level: trustedLevel,
              },
            };
          }

          return newState;
        },
        {},
      );

      const filteredState = Object.keys(state).reduce((newState, agentId) => {
        if (transformedPartnerMetadata[agentId]) {
          return {
            ...newState,
            [agentId]: transformedPartnerMetadata[agentId],
          };
        }
        return newState;
      }, state);

      return {
        ...filteredState,
        ...transformedPartnerMetadata,
      };
    }

    default:
      return state;
  }
};

const isPartnerMetadataQueryResponseAction = (
  action: PartnerMetadataAction,
): action is PartnerMetadataQueryResponseAction =>
  action.type === QUERY_RESPONSE_RECEIVED && 'payload' in action;

export const partnerMetadataReducer = (
  state: PartnerMetadataStateBookingPanel = INITIAL_STATE,
  action: PartnerMetadataAction,
): PartnerMetadataStateBookingPanel => {
  switch (action.type) {
    case QUERY_RESPONSE_RECEIVED: {
      if (isPartnerMetadataQueryResponseAction(action)) {
        const newById = byId(state.byId, action);
        if (newById === state.byId) return state;
        return { byId: newById };
      }
      return state;
    }
    default:
      return state;
  }
};
